import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CreateAgent.css';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaUserPlus, FaEdit, FaTrash, FaTimes, FaUsers, FaUsersCog, FaUserShield } from 'react-icons/fa';
import Button from './UI/Button';

// Add dummy data for experience users
const dummyAgents = [
  {
    _id: 'exp_agent_1',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    description: 'Senior support specialist with expertise in product returns and customer satisfaction.',
  },
  {
    _id: 'exp_agent_2',
    name: 'Michael Chen',
    email: 'michael.c@example.com',
    description: 'Technical support expert specializing in order processing and shipping issues.',
  },
  {
    _id: 'exp_agent_3',
    name: 'Emma Rodriguez',
    email: 'emma.r@example.com',
    description: 'Customer experience specialist focused on product inquiries and feedback management.',
  }
];

const dummyEmployeeStats = {
  'exp_agent_1': {
    ticket_stats: {
      total_assigned: 245,
      active_tickets: 12,
      resolution_rate: 92,
      avg_resolution_time_hours: 4.5
    },
    tags_stats: {
      tags_statistics: [
        { _id: 'Refund', percentage: 35 },
        { _id: 'Shipping', percentage: 25 },
        { _id: 'Product', percentage: 20 }
      ]
    }
  },
  'exp_agent_2': {
    ticket_stats: {
      total_assigned: 189,
      active_tickets: 8,
      resolution_rate: 88,
      avg_resolution_time_hours: 5.2
    },
    tags_stats: {
      tags_statistics: [
        { _id: 'Technical', percentage: 40 },
        { _id: 'Order', percentage: 30 },
        { _id: 'Shipping', percentage: 15 }
      ]
    }
  },
  'exp_agent_3': {
    ticket_stats: {
      total_assigned: 156,
      active_tickets: 5,
      resolution_rate: 95,
      avg_resolution_time_hours: 3.8
    },
    tags_stats: {
      tags_statistics: [
        { _id: 'Product', percentage: 45 },
        { _id: 'Feedback', percentage: 25 },
        { _id: 'General', percentage: 20 }
      ]
    }
  }
};

const CreateAgent = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(undefined);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [agents, setAgents] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [newDescription, setNewDescription] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [employeeStats, setEmployeeStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [areStatsLoaded, setAreStatsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      await fetchUserData(token);
      await fetchAgents(token);
    };

    fetchData();
  }, [navigate]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    }
  };

  const fetchEmployeeStats = async (token, employeeId) => {
    try {
      const response = await axios.get(`/api/tickets/employee-stats/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeStats(prev => ({
        ...prev,
        [employeeId]: response.data
      }));
    } catch (error) {
      console.error(`Failed to fetch stats for employee ${employeeId}:`, error);
    }
  };

  const fetchAgents = async (token) => {
    try {
      // Check if user is experience role
      const userDataString = localStorage.getItem('userData');
      let userRole = null;
      if (userDataString) {
        try {
          userRole = JSON.parse(userDataString).role;
        } catch (parseError) {
          console.error("Failed to parse user data for CreateAgent:", parseError);
        }
      }

      if (userRole === 'experience') {
        console.log("Experience user detected, loading dummy agent data.");
        setAgents(dummyAgents);
        setEmployeeStats(dummyEmployeeStats);
        setAreStatsLoaded(true);
        setIsLoading(false);
        return;
      }

      // Regular flow for non-experience users
      const response = await axios.get('/api/employees/agents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents(response.data);
      
      setAreStatsLoaded(false);
      
      const statsPromises = response.data.map(agent => 
        fetchEmployeeStats(token, agent._id)
      );
      
      await Promise.all(statsPromises);
      setAreStatsLoaded(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setIsLoading(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/employees/create-agent',
        { name, email, description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setName('');
      setEmail('');
      setDescription('');
      setCreateModalOpen(false);
      fetchAgents(token);
      alert('Agent created successfully');
    } catch (error) {
      console.error('Failed to create agent:', error);
    }
  };

  const handleEditDescription = (agent) => {
    setCurrentAgent(agent);
    setNewDescription(agent.description);
    setNewName(agent.name);
    setNewEmail(agent.email);
    setEditModalOpen(true);
  };

  const handleDeleteAgent = async (agentEmail) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/employees/delete-employee/${agentEmail}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAgents(token);
      alert('Agent deleted successfully');
    } catch (error) {
      console.error('Failed to delete agent:', error);
    }
  };

  const handleUpdateDescription = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Prepare the update payload
      const updatePayload = {
        email: currentAgent.email, // Current email to identify the employee
      };

      // Only include fields that have changed
      if (newName !== currentAgent.name) {
        updatePayload.name = newName;
      }
      if (newEmail !== currentAgent.email) {
        updatePayload.new_email = newEmail;
      }
      if (newDescription !== currentAgent.description) {
        updatePayload.description = newDescription;
      }

      await axios.put(
        '/api/employees/update-employee',
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditModalOpen(false);
      fetchAgents(token);
      alert('Employee updated successfully');
    } catch (error) {
      console.error('Failed to update employee:', error);
      alert('Failed to update employee');
    }
  };

  if (user === undefined || isLoading || !areStatsLoaded) {
    return (
      <div className="una-employee-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('createAgent.loadingEmployees')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaUsers />
              <span>{t('createAgent.loadingEmployeeData')}</span>
            </div>
            <div className="una-metric-item">
              <FaUsersCog />
              <span>{t('createAgent.processingPermissions')}</span>
            </div>
            <div className="una-metric-item">
              <FaUserShield />
              <span>{t('createAgent.loadingRoles')}</span>
            </div>
            <div className="una-metric-item">
              <FaUserPlus />
              <span>{t('createAgent.preparingDashboard')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (user.subscription_plan === 'free') {
    return (
      <div className="create-agent-container">
        <h2 className="page-title">{t('createAgent.manageEmployees')}</h2>
        <div className="not-accessible">
          <p>{t('createAgent.notAccessible')}</p>
          <Link to="/billing" className="upgrade-link">{t('createAgent.upgrade')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-management-container">
      <div className="employee-dashboard-card">

        <Button 
          variant="primary" 
          size="medium" 
          onClick={() => setCreateModalOpen(true)}
          className="employee-add-button"
        >
          <FaUserPlus /> {t('createAgent.createEmployee')}
        </Button>

        <div className="employee-grid">
          {agents.map((agent) => (
            <div key={agent._id} className="employee-card">
              <div className="employee-header">
                <div className="employee-info">
                  <div className="employee-name">{agent.name}</div>
                  <div className="employee-email">{agent.email}</div>
                </div>
                <div className="employee-actions">
                  <Button 
                    variant="secondary"
                    size="small"
                    onClick={() => handleEditDescription(agent)}
                    aria-label={t('createAgent.editEmployee')}
                    className="employee-action-button edit"
                  >
                    <FaEdit />
                  </Button>
                  <Button 
                    variant="danger"
                    size="small"
                    onClick={() => handleDeleteAgent(agent.email)}
                    aria-label={t('createAgent.deleteEmployee')}
                    className="employee-action-button delete"
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>
              <div className="employee-description">
                {agent.description}
              </div>
              {employeeStats[agent._id] && (
                <div className="employee-stats">
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-label">{t('createAgent.totalTickets')}</span>
                      <span className="stat-value">{employeeStats[agent._id].ticket_stats.total_assigned}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">{t('createAgent.activeTickets')}</span>
                      <span className="stat-value">{employeeStats[agent._id].ticket_stats.active_tickets}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">{t('createAgent.resolutionRate')}</span>
                      <span className="stat-value">{employeeStats[agent._id].ticket_stats.resolution_rate}%</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">{t('createAgent.avgResolutionTime')}</span>
                      <span className="stat-value">
                        {employeeStats[agent._id].ticket_stats.avg_resolution_time_hours.toFixed(1)}h
                      </span>
                    </div>
                  </div>
                  
                  {employeeStats[agent._id].tags_stats.tags_statistics.length > 0 && (
                    <div className="tags-stats">
                      <span className="stat-label">{t('createAgent.topTags')}</span>
                      <div className="tags-list">
                        {employeeStats[agent._id].tags_stats.tags_statistics
                          .slice(0, 3)
                          .map(tag => (
                            <span key={tag._id} className="tag">
                              {tag._id} ({tag.percentage}%)
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {createModalOpen && (
          <div className="employee-create-modal">
            <div className="employee-modal-content">
              <h3>{t('createAgent.createEmployee')}</h3>
              <form onSubmit={handleCreateAgent} className="employee-create-form">
                <div className="employee-form-group">
                  <label htmlFor="name">{t('createAgent.name')}</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="employee-form-group">
                  <label htmlFor="email">{t('createAgent.email')}</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="employee-form-group">
                  <label htmlFor="description">{t('createAgent.description')}</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows="4"
                  ></textarea>
                </div>
                <div className="employee-modal-actions">
                  <Button 
                    type="button" 
                    variant="secondary"
                    size="medium"
                    onClick={() => setCreateModalOpen(false)}
                    className="employee-cancel-button"
                  >
                    {t('createAgent.cancel')}
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary"
                    size="medium"
                    className="employee-submit-button"
                  >
                    {t('createAgent.create')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editModalOpen && currentAgent && (
          <div className="employee-create-modal">
            <div className="employee-modal-content">
              <div className="employee-modal-header">
                <h3>{t('createAgent.editEmployee')}</h3>
                <Button 
                  variant="outline"
                  size="small"
                  onClick={() => setEditModalOpen(false)}
                  aria-label="Close modal"
                  className="employee-modal-close"
                >
                  <FaTimes />
                </Button>
              </div>
              <div className="employee-edit-form">
                <div className="employee-form-group">
                  <label htmlFor="edit-name">{t('createAgent.name')}</label>
                  <input
                    type="text"
                    id="edit-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    required
                  />
                </div>
                <div className="employee-form-group">
                  <label htmlFor="edit-email">{t('createAgent.email')}</label>
                  <input
                    type="email"
                    id="edit-email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="employee-form-group">
                  <label htmlFor="edit-description">{t('createAgent.description')}</label>
                  <textarea
                    id="edit-description"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    required
                    rows="4"
                  ></textarea>
                </div>
                <div className="employee-modal-actions">
                  <Button 
                    type="button" 
                    variant="secondary"
                    size="medium"
                    onClick={() => setEditModalOpen(false)}
                    className="employee-cancel-button"
                  >
                    {t('createAgent.cancel')}
                  </Button>
                  <Button 
                    type="button" 
                    variant="primary"
                    size="medium"
                    onClick={handleUpdateDescription}
                    className="employee-submit-button"
                  >
                    {t('createAgent.update')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateAgent;